import { useEffect } from 'react';
import { getTokenExpiration } from '../utils/auth/tokenUtils';
import { logger } from '../utils/logger';

/** setTimeout 32-bit signed ceiling (~24.8 days). Larger delays overflow and fire immediately. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Schedules an auto-logout when the JWT's `exp` claim is reached.
 *
 * Behavior:
 * - null token → no-op (nothing to schedule).
 * - Token without an `exp` claim (non-JWT, malformed, or missing exp) → no-op,
 *   we can't know when to expire it. Server 401 remains the safety net.
 * - Token with future exp → schedules setTimeout in capped chunks until expiry.
 * - Token with past exp → invokes logout synchronously on mount.
 *
 * The effect re-runs when token or logout changes; the cleanup clears any
 * pending timer so token rotation does not leave stale logouts queued.
 *
 * Caller contract: pass a stable `logout` reference (e.g. via useCallback)
 * to avoid the effect re-running on every render.
 */
export const useAutoLogoutOnExpiry = (
  token: string | null,
  logout: () => void
): void => {
  useEffect(() => {
    if (!token) return;
    const expiresAt = getTokenExpiration(token);
    if (!expiresAt) return;

    let timeoutId: number;

    const schedule = () => {
      const msUntilExpiry = expiresAt - Date.now();
      if (msUntilExpiry <= 0) {
        logger.info('Access token expired; logging out');
        logout();
        return;
      }
      timeoutId = window.setTimeout(
        schedule,
        Math.min(msUntilExpiry, MAX_TIMEOUT_MS)
      );
    };

    schedule();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [token, logout]);
};
